import React from "react";

import * as hardware from "@client/hardware";
import * as forms from "@client/site/forms";
import {classNames} from "@reactivated";

import {
    SegKey,
    SegValues,
    displaySegment,
    formatSeconds,
    handleBackspace,
    handleInput,
    joinSeconds,
    maxFor,
    segmentOrder,
    smartFocusTarget,
    splitSeconds,
    step,
} from "./machine";

const IS_IOS =
    typeof navigator !== "undefined" && /iP(hone|od|ad)/.test(navigator.userAgent);

const SEG_NAMES: Record<SegKey, string> = {
    hh: "hours",
    mm: "minutes",
    ss: "seconds",
};

export function SegTime(props: {
    name: string;
    label: string;
    value: DecimalValue | null;
    onChange: (value: DecimalValue | null) => void;
    hours?: boolean;
    computed?: boolean;
    readOnly?: boolean;
    // Drop the field chrome (ring/background/rounding) and left-align so the
    // widget can sit inside a connected group such as the cardio Triad cell.
    bare?: boolean;
    className?: string;
    suffix?: React.ReactNode;
    onSuffixTap?: () => void;
}) {
    const hours = props.hours ?? false;
    const order = segmentOrder(hours);
    const externalSeconds = props.value == null ? null : parseInt(props.value, 10);

    const [vals, setVals] = React.useState<SegValues>(() =>
        splitSeconds(externalSeconds, hours),
    );
    const [focused, setFocused] = React.useState<SegKey | null>(null);

    const valsRef = React.useRef(vals);
    valsRef.current = vals;
    const entered = React.useRef<Partial<Record<SegKey, string>>>({});
    const nodes = React.useRef<Partial<Record<SegKey, HTMLSpanElement | null>>>({});
    const compositionSnapshot = React.useRef("");
    const containerRef = React.useRef<HTMLDivElement>(null);
    const lastEmitted = React.useRef<number | null>(joinSeconds(vals));

    // Resync from outside (write-through, resets) without clobbering mid-entry.
    React.useEffect(() => {
        if (externalSeconds !== lastEmitted.current) {
            lastEmitted.current = externalSeconds;
            setVals(splitSeconds(externalSeconds, hours));
        }
    }, [externalSeconds, hours]);

    const commit = (next: SegValues) => {
        setVals(next);
        const seconds = joinSeconds(next);
        lastEmitted.current = seconds;
        props.onChange(seconds == null ? null : seconds.toString());
    };

    const focusSegment = (key: SegKey) => nodes.current[key]?.focus();

    const focusSibling = (key: SegKey, delta: 1 | -1) => {
        const index = order.indexOf(key) + delta;
        if (index >= 0 && index < order.length) {
            focusSegment(order[index]);
        }
    };

    const editable = props.readOnly != true && props.computed != true;

    const onInput = (key: SegKey, data: string) => {
        if (editable == false) {
            return;
        }

        const result = handleInput(
            valsRef.current,
            key,
            entered.current[key] ?? "",
            data,
            hours,
        );

        if (result == null) {
            return;
        }

        commit(result.vals);
        entered.current[key] = result.enteredKeys;

        if (result.advance) {
            focusSibling(key, 1);
        }
    };

    const backspace = (key: SegKey) => {
        if (editable == false) {
            return;
        }

        const result = handleBackspace(valsRef.current, key);

        if (result.hop) {
            focusSibling(key, -1);
            return;
        }

        commit(result.vals);
        entered.current[key] = result.enteredKeys;
    };

    // Native beforeinput/input wiring: mobile keyboards delete via inputType
    // (no keydown), and some (Pinyin iOS, Gboard) deliver digits as composition.
    React.useEffect(() => {
        const cleanups = order.map((key) => {
            const el = nodes.current[key];

            if (el == null || editable == false) {
                return () => {};
            }

            const onBeforeInput = (event: InputEvent) => {
                event.preventDefault();

                switch (event.inputType) {
                    case "deleteContentBackward":
                    case "deleteContentForward":
                        backspace(key);
                        break;
                    case "insertCompositionText":
                        // Cannot be canceled: snapshot DOM, self-assign to
                        // un-stick Safari composition; restored in `input`.
                        compositionSnapshot.current = el.textContent;
                        // eslint-disable-next-line no-self-assign
                        el.textContent = el.textContent;
                        break;
                    default:
                        if (event.data != null) {
                            onInput(key, event.data);
                        }
                }
            };

            const onNativeInput = (event: Event) => {
                const inputEvent = event as InputEvent;

                if (inputEvent.inputType === "insertCompositionText") {
                    el.textContent = compositionSnapshot.current;
                    const digits = (inputEvent.data ?? "").replace(/\D/g, "");

                    if (digits !== "") {
                        onInput(key, digits);
                    }
                }
            };

            el.addEventListener("beforeinput", onBeforeInput);
            el.addEventListener("input", onNativeInput);

            return () => {
                el.removeEventListener("beforeinput", onBeforeInput);
                el.removeEventListener("input", onNativeInput);
            };
        });

        return () => cleanups.forEach((cleanup) => cleanup());
    });

    // Never allow a selection inside segments (Android Chrome crash guard).
    React.useEffect(() => {
        const handler = () => {
            const selection = window.getSelection();

            if (
                selection?.anchorNode != null &&
                containerRef.current?.contains(selection.anchorNode) === true &&
                selection.isCollapsed === false
            ) {
                selection.collapse(selection.anchorNode);
            }
        };

        document.addEventListener("selectionchange", handler);
        return () => document.removeEventListener("selectionchange", handler);
    }, []);

    const onKeyDown = (key: SegKey) => (event: React.KeyboardEvent) => {
        switch (event.key) {
            case "Backspace":
            case "Delete":
                event.preventDefault();
                backspace(key);
                break;
            case "ArrowLeft":
                event.preventDefault();
                focusSibling(key, -1);
                break;
            case "ArrowRight":
            case "Enter":
                event.preventDefault();
                focusSibling(key, 1);
                break;
            case "ArrowUp":
            case "ArrowDown":
                event.preventDefault();

                if (editable) {
                    entered.current[key] = "";
                    commit(
                        step(
                            valsRef.current,
                            key,
                            event.key === "ArrowUp" ? 1 : -1,
                            hours,
                        ),
                    );
                }
                break;
        }
    };

    const onFocusSegment = (key: SegKey) => (event: React.FocusEvent) => {
        entered.current[key] = ""; // fresh entry session — replaces select()-on-focus
        setFocused(key);
        event.currentTarget.scrollIntoView({block: "nearest"});
        // Collapse selection to the segment or Chrome won't fire input events.
        window.getSelection()?.collapse(nodes.current[key] ?? null);
    };

    const bare = props.bare ?? false;
    const fieldClass = bare
        ? "block w-full text-gray-900"
        : forms.getFieldClass({readOnly: props.readOnly});

    if (props.computed === true) {
        return (
            <div
                className={classNames(
                    fieldClass,
                    "flex items-center justify-center bg-indigo-50! font-semibold",
                    "rounded-md! text-indigo-600 ring-indigo-200!",
                    props.className,
                )}
            >
                {formatSeconds(externalSeconds)}
            </div>
        );
    }

    return (
        <div
            ref={containerRef}
            role="group"
            aria-label={props.label}
            onMouseDown={(event) => {
                // Tap on chrome/colons (segments stopPropagation): smart focus.
                if (editable) {
                    event.preventDefault();
                    focusSegment(smartFocusTarget(valsRef.current, hours));
                }
            }}
            className={classNames(
                fieldClass,
                "flex cursor-text items-center gap-px",
                bare ? "justify-start" : "justify-center",
                props.suffix == null && bare == false && "rounded-md!",
                props.className,
            )}
        >
            <input type="hidden" name={props.name} value={props.value ?? ""} />
            {order.flatMap((key, index) => {
                const display = displaySegment(vals, key, hours);
                const isPlaceholder = display == null;

                const segment = (
                    <span
                        key={key}
                        ref={(el) => {
                            nodes.current[key] = el;
                        }}
                        contentEditable={editable}
                        suppressContentEditableWarning
                        inputMode="numeric"
                        tabIndex={0}
                        spellCheck={false}
                        autoCorrect="off"
                        enterKeyHint="next"
                        role={IS_IOS ? "textbox" : "spinbutton"}
                        aria-label={`${SEG_NAMES[key]}, ${props.label}`}
                        aria-valuenow={IS_IOS ? undefined : (vals[key] ?? undefined)}
                        aria-valuetext={
                            IS_IOS
                                ? undefined
                                : isPlaceholder
                                  ? "Empty"
                                  : String(vals[key])
                        }
                        aria-valuemin={IS_IOS ? undefined : 0}
                        aria-valuemax={
                            IS_IOS ? undefined : (maxFor(key, vals, hours) ?? 99)
                        }
                        onKeyDown={onKeyDown(key)}
                        onFocus={onFocusSegment(key)}
                        onBlur={() =>
                            setFocused((current) => (current === key ? null : current))
                        }
                        onMouseDown={(event) => event.stopPropagation()}
                        onPointerDown={(event) => event.stopPropagation()}
                        className={classNames(
                            "select-none rounded-xs text-center outline-none",
                            "[caret-color:transparent]",
                            key === "hh" ? "min-w-3" : "min-w-5",
                            isPlaceholder ? "text-gray-400" : "font-medium",
                            focused === key && "bg-indigo-100",
                        )}
                    >
                        {display ?? (key === "hh" ? "h" : key)}
                    </span>
                );

                const colon =
                    index < order.length - 1 ? (
                        <span
                            key={`colon-${index}`}
                            aria-hidden="true"
                            className="pointer-events-none text-gray-400"
                        >
                            :
                        </span>
                    ) : null;

                return colon == null ? [segment] : [segment, colon];
            })}
            {props.suffix != null && (
                <button
                    type="button"
                    onClick={() => {
                        hardware.haptic("Light");
                        props.onSuffixTap?.();
                    }}
                    disabled={props.onSuffixTap == null || props.readOnly}
                    onMouseDown={(event) => event.stopPropagation()}
                    className={classNames(
                        forms.getSuffixClass({readOnly: props.readOnly}),
                        "-mr-2 ml-1 h-full border-0! sm:-mr-3",
                        props.onSuffixTap != null && "font-semibold text-indigo-600",
                    )}
                >
                    {props.suffix}
                </button>
            )}
        </div>
    );
}
